import * as Sentry from "@sentry/vue";

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;

export interface ResubscribeScheduler {
  /** The cable gave up on the subscription: report the loss, schedule a re-create. */
  failed: (error: Error) => void;
  /** The subscription connected: the streak is over and nothing is pending. */
  connected: () => void;
  /** Deliberate teardown: drop a pending re-create. */
  cancel: () => void;
}

interface ResubscribeSchedulerOptions {
  /** Names the subscription in the console, e.g. the channel identifier. */
  label: string;
  /** Sentry tags naming the owner; the `phase` tag is added here. */
  tags: Record<string, string>;
  /** Creates the subscription anew. Always called from a timer. */
  resubscribe: () => void;
}

/**
 * Re-creates a cable subscription the client gave up on.
 *
 * AnyCable drops a subscription for good when its `subscribe` gets no ack
 * within 10s: it closes the channel with a SubscriptionTimeoutError and removes
 * it from its hub, and a reconnect restores only what is still in the hub. So
 * the composable that owns the subscription has to re-create it, and this
 * holds the part that is easy to get wrong:
 *
 * - Never from inside the `close` event. The cable emits it one line before it
 *   removes the dead subscription from its hub, and a subscribe issued in
 *   between attaches to that disposed subscription and throws. The re-create
 *   is always a timer.
 * - Back off. The delay doubles per consecutive failure (1s, 2s, 4s …, capped
 *   at 30s) and resets on a connect, so a server that has stopped acking is
 *   asked again by every open tab at most every 30s.
 * - Report the first loss of a streak to Sentry and every one to the console.
 *   A server that has stopped acking would otherwise send an event per tab per
 *   retry for as long as it lasts.
 */
export function createResubscribeScheduler({
  label,
  tags,
  resubscribe,
}: ResubscribeSchedulerOptions): ResubscribeScheduler {
  let consecutiveFailures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const cancel = (): void => {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
  };

  const runResubscribe = (): void => {
    timer = undefined;
    try {
      resubscribe();
    } catch (error: unknown) {
      // Left uncaught, a throw here would end the retry chain without a trace.
      console.error(`[cable][sub] resubscribe-failed: ${label}`, error);
      Sentry.captureException(error, {
        tags: { ...tags, phase: "resubscribe" },
      });
    }
  };

  return {
    failed(error) {
      consecutiveFailures += 1;
      console.error(`[cable][sub] lost: ${label}`, error);
      if (consecutiveFailures === 1) {
        Sentry.captureException(error, {
          tags: { ...tags, phase: "subscribe" },
        });
      }

      cancel();
      timer = setTimeout(runResubscribe, delayMs(consecutiveFailures));
    },
    connected() {
      consecutiveFailures = 0;
      cancel();
    },
    cancel,
  };
}

function delayMs(consecutiveFailures: number): number {
  return Math.min(BASE_DELAY_MS * 2 ** (consecutiveFailures - 1), MAX_DELAY_MS);
}
